import React, { useState } from "react";
import { BakeryItem, ProductionsData } from "../types";
import Freshness from "./Freshness";
import { uploadRotationClearance } from "../backendServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faTriangleExclamation,
  faCheck,
  faShare
} from "@fortawesome/pro-solid-svg-icons";
import { currentDateTime } from "./functions";
import { useSnackbar } from "notistack";

interface HighRiskRotationBarProps {
  production: ProductionsData;
  bakeTableState: BakeryItem[];
  onExpireRefresh: () => void;
}

const HighRiskRotationBar: React.FC<HighRiskRotationBarProps> = ({
  production,
  bakeTableState,
  onExpireRefresh
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const getSentDate = (timestamp: string) => {
    const date = new Date(timestamp).getTime();
    return date;
  };

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleUndoClearRotation = async (PLU: number, timestamp: string) => {
    await uploadRotationClearance({ PLU, timestamp })
      .then((response: any) => {
        setIsProcessing(false);
        if (response.status === 200) {
          setIsSuccess(true);
          setTimeout(() => {
            setIsSuccess(false);
            onExpireRefresh();
          }, 1000);
        } else {
          console.error("Undo clearance error (status not 200)");
          setIsError(true);
          setTimeout(() => {
            setIsError(false);
          }, 3000);
        }
      })
      .catch((error: string) => {
        setIsProcessing(false);
        console.error("Undo clearance error");
        setIsError(true);
        setTimeout(() => {
          setIsError(false);
        }, 1000);
      });
  };

  const handleClearRotation = async (PLU: number, timestamp: string) => {
    const ClearanceTime = currentDateTime();
    await uploadRotationClearance({ PLU, timestamp, ClearanceTime })
      .then((response: any) => {
        setIsProcessing(false);
        if (response.status === 200) {
          setIsSuccess(true);
          enqueueSnackbar(
            `${production.product_name} rotation timer cleared! Please remember to take the appropriate action on the till and Vita Mojo Hub.`,
            {
              variant: "warning",
              transitionDuration: { enter: 0, exit: 0 },
              style: {
                top: "50vh",
                transform: "translateY(-50%)"
              },
              anchorOrigin: { horizontal: "center", vertical: "top" },
              action: (key) => (
                <button
                  className="button--amber"
                  onClick={() => closeSnackbar(key)}
                >
                  OK
                </button>
              ),
              persist: true,
              onClose: (event, reason, key) => {
                onExpireRefresh();
                setIsSuccess(false);
              }
            }
          );
        } else {
          console.error("Clearance error (status not 200)");
          setIsError(true);
          setTimeout(() => {
            setIsError(false);
          }, 3000);
        }
      })
      .catch((error: string) => {
        setIsProcessing(false);
        console.error("Clearance error");
        setIsError(true);
        setTimeout(() => {
          setIsError(false);
        }, 3000);
      });
  };

  return (
    <div className="hlpr-flexbox bakery-table--high-risk-rotation__freshness">
      {production.high_risk_rotation_clearance ? (
        <button
          className="hlpr-clear-input clear-rotation"
          onClick={() =>
            handleUndoClearRotation(production.PLU, production.timestamp)
          }
          disabled={isProcessing || isSuccess || isError}
        >
          {isSuccess && <FontAwesomeIcon icon={faCheck} />}
          {isError && <FontAwesomeIcon icon={faTriangleExclamation} />}
          {!(isProcessing || isSuccess || isError) && (
            <FontAwesomeIcon icon={faShare} />
          )}
        </button>
      ) : (
        <>
          <Freshness
            targetFresh={
              bakeTableState.filter((item) => item.PLU === production.PLU)[0]
                .target_fresh
            }
            sentDate={getSentDate(production.timestamp)}
            endHour={23}
          />

          <button
            className="hlpr-clear-input clear-rotation"
            onClick={() =>
              handleClearRotation(production.PLU, production.timestamp)
            }
            disabled={isProcessing || isSuccess || isError}
          >
            {isSuccess && <FontAwesomeIcon icon={faCheck} />}
            {isError && <FontAwesomeIcon icon={faTriangleExclamation} />}
            {!(isProcessing || isSuccess || isError) && (
              <FontAwesomeIcon icon={faXmark} />
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default HighRiskRotationBar;
